// src/pages/userprofile/UserprofileCreatePage.tsx
// Profile creation page with banner, avatar upload, and sectioned form.

import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useUserprofile, useUserprofileAvatar, useI18n } from '@react/hooks';
import {
  Button, Input, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, ErrorAlert,
} from '@react/components';
import { Textarea } from '@react/components';
import { Select } from '@/components/Select';
import { cn } from '@core/ui';
import {
  validateProfile, getProfileInitials, genderOptions, bannerGradients,
  languageOptions, timezoneOptions, accentColorOptions,
} from '../../features/userprofile/logic';

// ─── Preview Header ────────────────────────────────────────────────

function CreateProfileHeader({ form, avatarPreview, bannerPreview, bannerGradient, onAvatarChange, onBannerChange, onBannerRemove, t }: {
  form: { firstName: string; lastName: string; displayName: string; bio: string; location: string };
  avatarPreview: string | null;
  bannerPreview: string | null;
  bannerGradient: string;
  onAvatarChange: (file: File) => void;
  onBannerChange: (file: File) => void;
  onBannerRemove: () => void;
  t: (key: string) => string;
}) {
  const displayName = form.displayName || [form.firstName, form.lastName].filter(Boolean).join(' ') || t('userprofile.newProfile');
  const initials = getProfileInitials(form.firstName || '?', form.lastName || '?');
  const bannerInputRef = useRef<HTMLInputElement>(null);

  return (
    <Card className="overflow-hidden">
      {/* Banner */}
      <div className="relative group/banner">
        {bannerPreview ? (
          <div className="h-32 sm:h-40 overflow-hidden">
            <img src={bannerPreview} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className={cn('h-32 sm:h-40 bg-gradient-to-br', bannerGradient)} />
        )}

        {/* Banner controls overlay */}
        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-foreground/0 group-hover/banner:bg-foreground/30 transition-colors">
          <div className="flex gap-2 opacity-0 group-hover/banner:opacity-100 transition-opacity">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => bannerInputRef.current?.click()}
              className="shadow-md"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {t('userprofile.changeBanner')}
            </Button>
            {bannerPreview && (
              <Button size="sm" variant="danger" onClick={onBannerRemove} className="shadow-md">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </Button>
            )}
          </div>
        </div>

        <input
          ref={bannerInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onBannerChange(f);
            e.target.value = '';
          }}
        />
      </div>

      <div className="px-4 pb-5 sm:px-6 sm:pb-6">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12 sm:-mt-14">
          {/* Avatar */}
          <div className="relative shrink-0 self-center sm:self-auto">
            <div className={cn(
              'relative w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden border-4 border-card shadow-lg cursor-pointer',
              'hover:ring-2 hover:ring-primary/50 transition-all group/avatar',
            )}>
              {avatarPreview ? (
                <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <div className="flex items-center justify-center w-full h-full bg-muted">
                  <span className="text-2xl font-bold text-muted-foreground">{initials}</span>
                </div>
              )}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/avatar:opacity-100 transition-opacity flex items-center justify-center rounded-full">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onAvatarChange(f);
                  e.target.value = '';
                }}
              />
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0 text-center sm:text-left pb-1">
            <h1 className="text-xl sm:text-2xl font-bold text-foreground truncate">
              {displayName}
            </h1>
            {form.bio && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{form.bio}</p>
            )}
            {form.location && (
              <div className="flex items-center justify-center sm:justify-start gap-1 mt-2 text-xs text-muted-foreground">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {form.location}
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────

export const UserprofileCreatePage = observer(() => {
  const profileStore = useUserprofile();
  const avatarStore = useUserprofileAvatar();
  const { t } = useI18n();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: '', lastName: '', middleName: '', displayName: '', bio: '',
    location: '', gender: '', dateOfBirth: '', language: '', timezone: '',
  });
  const [errors, setErrors] = useState<Record<string, unknown>>({});
  const [bannerGradient, setBannerGradient] = useState<string>(bannerGradients[0].value);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [accentColor, setAccentColor] = useState('');

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleAvatarChange = (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, avatar: t('userprofile.avatarTooLarge') }));
      return;
    }
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleBannerChange = (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, banner: t('userprofile.bannerTooLarge') }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setBannerPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleCreate = async () => {
    const validation = validateProfile(form);
    if (Object.keys(validation).length > 0) {
      setErrors(Object.fromEntries(Object.entries(validation).map(([k, v]) => [k, t(v)])));
      return;
    }

    try {
      const result = await profileStore.CreateUserprofile({
        userprofile: {
          entity: {} as never,
          first_name: form.firstName,
          last_name: form.lastName,
          middle_name: form.middleName || undefined,
          display_name: form.displayName,
          bio: form.bio,
          user: {} as never,
          privacy_settings: {} as never,
          ui_settings: {} as never,
          status: 'PROFILE_STATUS_ACTIVE',
          is_default: false,
          location: form.location || undefined,
          gender: form.gender || undefined,
          date_of_birth: form.dateOfBirth ? new Date(form.dateOfBirth).toISOString() : undefined,
          language: form.language || undefined,
          timezone: form.timezone || undefined,
          resources: {} as never,
          aspects: {} as never,
        } as never,
      });

      // Upload avatar after profile is created
      const profileId = (result as any)?.entity?.id;
      if (profileId && avatarFile) {
        try {
          const avatar = await avatarStore.CreateAvatar(avatarFile);
          if (avatar?.entity?.id) {
            await avatarStore.ActivateAvatar(profileId, avatar.entity.id, { profile: {} as never, avatar: {} as never });
          }
        } catch {
          // Avatar upload failed but profile was created — continue
        }
      }

      navigate('/userprofiles');
    } catch (err) {
      setErrors({ form: err });
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Link to="/userprofiles" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          {t('common.backToList')}
        </Link>
      </div>

      {/* Page title */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground">{t('userprofile.create')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('userprofile.fillInfo')}</p>
      </div>

      {errors.form ? <ErrorAlert error={errors.form} dismissible onDismiss={() => setErrors((p) => ({ ...p, form: null }))} /> : null}

      {/* Live preview header with avatar/banner upload */}
      <CreateProfileHeader
        form={form}
        avatarPreview={avatarPreview}
        bannerPreview={bannerPreview}
        bannerGradient={bannerGradient}
        onAvatarChange={handleAvatarChange}
        onBannerChange={handleBannerChange}
        onBannerRemove={() => setBannerPreview(null)}
        t={t}
      />

      {/* Appearance section — banner gradient + accent color */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('userprofile.appearance')}</CardTitle>
          <CardDescription>{t('userprofile.appearanceDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Banner gradient */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('userprofile.gradientDefault')}</label>
            <div className="flex flex-wrap gap-2">
              {bannerGradients.map((g) => (
                <button
                  key={g.value}
                  type="button"
                  onClick={() => { setBannerGradient(g.value); setBannerPreview(null); }}
                  className={cn(
                    'w-16 h-10 rounded-lg bg-gradient-to-br transition-all',
                    g.value,
                    bannerGradient === g.value && !bannerPreview
                      ? 'ring-2 ring-primary ring-offset-2 ring-offset-background'
                      : 'hover:ring-1 hover:ring-border',
                  )}
                  title={t(g.labelKey)}
                />
              ))}
            </div>
          </div>

          {/* Accent color */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('userprofile.accentColor')}</label>
            <div className="flex flex-wrap gap-2">
              {accentColorOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setAccentColor(opt.value)}
                  className={cn(
                    'w-8 h-8 rounded-full transition-all',
                    accentColor === opt.value
                      ? 'ring-2 ring-offset-2 ring-offset-background'
                      : 'hover:scale-110',
                  )}
                  style={{
                    backgroundColor: opt.value,
                    ...(accentColor === opt.value ? { ringColor: opt.value } : {}),
                  }}
                  title={opt.label}
                />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Name section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('userprofile.nameSection')}</CardTitle>
          <CardDescription>{t('userprofile.nameSectionDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label={t('userprofile.firstName')} value={form.firstName} onChange={handleChange('firstName')} error={errors.firstName as string | undefined} required />
            <Input label={t('userprofile.lastName')} value={form.lastName} onChange={handleChange('lastName')} error={errors.lastName as string | undefined} required />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label={t('userprofile.middleName')} value={form.middleName} onChange={handleChange('middleName')} />
            <Input label={t('userprofile.displayName')} value={form.displayName} onChange={handleChange('displayName')} error={errors.displayName as string | undefined} placeholder={t('userprofile.displayNamePlaceholder')} />
          </div>
        </CardContent>
      </Card>

      {/* About section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('userprofile.aboutSection')}</CardTitle>
          <CardDescription>{t('userprofile.aboutSectionDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            label={t('userprofile.bio')}
            value={form.bio}
            onChange={handleChange('bio')}
            error={errors.bio as string | undefined}
            helperText={`${form.bio.length}/256`}
            rows={4}
            placeholder={t('userprofile.bioPlaceholder')}
          />
          <Input label={t('userprofile.location')} value={form.location} onChange={handleChange('location')} placeholder={t('userprofile.locationPlaceholder')} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{t('userprofile.gender')}</label>
              <Select value={form.gender} onChange={handleChange('gender')}>
                {genderOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                ))}
              </Select>
            </div>
            <Input label={t('userprofile.dateOfBirth')} type="date" value={form.dateOfBirth} onChange={handleChange('dateOfBirth')} />
          </div>
        </CardContent>
      </Card>

      {/* Preferences section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('userprofile.preferences')}</CardTitle>
          <CardDescription>{t('userprofile.preferencesDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{t('userprofile.language')}</label>
              <Select
                value={form.language}
                onChange={handleChange('language')}
                placeholder={t('userprofile.selectLanguage')}
              >
                <option value="">{t('userprofile.selectLanguage')}</option>
                {languageOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">{t('userprofile.timezone')}</label>
              <Select
                value={form.timezone}
                onChange={handleChange('timezone')}
                placeholder={t('userprofile.selectTimezone')}
              >
                <option value="">{t('userprofile.selectTimezone')}</option>
                {timezoneOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardFooter className="flex justify-end gap-2 py-4">
          <Link to="/userprofiles">
            <Button variant="secondary">{t('common.cancel')}</Button>
          </Link>
          <Button onClick={handleCreate} isLoading={profileStore.isSaving}>
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            {t('common.create')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
});

export default UserprofileCreatePage;
